import { useContentfulImages } from '@snapchat/mw-contentful-client';
import {
  imageHeightForImageSize,
  Tile as TileSDS,
  TileImageFit,
  TileImageSize,
} from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { AppContext } from '../../AppContext';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logUserEvent } from '../../helpers/logging';
import type { ContentfulIdVariable } from '../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { UserAction } from '../../types/events';
import { isContentfulSysNotShallow } from '../../utils/contentful';
import { parseMedia } from '../../utils/media';
import { renderRichTextMarkingsOnly } from '../../utils/renderText/renderRichText';
import { queries } from './query';
import type { TileDataHandlerProps, TileDataProps, TileProps } from './types';

/**
 * Tile. Unused.
 *
 * TODO: Delete this. Unless we want to refactor this out to be used as a child of Hero Media.
 */
export const Tile: FC<TileProps> = props => {
  const { isRTL } = useContext(AppContext);
  const { getImageSources } = useContentfulImages();
  const elementLocation = useContext(ElementLocation).elementLocation;

  const { data: rawData } = useContentfulQuery<TileDataHandlerProps, ContentfulIdVariable>(
    queries.all,
    {
      skip: isContentfulSysNotShallow(props),
      variables: { id: props.sys.id },
    }
  );

  const data = rawData?.tile ?? (props as TileDataProps);

  if (!data) {
    return null;
  }

  const { url: urlFromProps } = props;

  const {
    title,
    label,
    media,
    imageSize: queryImageSize,
    imageFit: queryImageFit,
    url: urlFromData,
  } = data;
  const {
    afterClick,
    beforeClick,
    onClick,
    imageSize: propsImageSize,
    imageFit: propsImageFit,
  } = props;

  const { imageSource, imageAltText } = parseMedia(media?.media);

  const onClickWrapped = (): void => {
    beforeClick?.();
    onClick?.();

    logUserEvent({
      eventCategory: 'Tile',
      eventAction: UserAction.Click,
      context: {
        targetUrl: url,
        elementLocation,
      },
    });

    afterClick?.();
  };

  const imageSize = queryImageSize ?? propsImageSize;
  const imageFit = queryImageFit ?? propsImageFit;
  const url = urlFromData ?? urlFromProps;

  // we do x 2 to handle higher DPI screens (e.g. iPhones) and also if we match height exactly
  // some uploaded images are too narrow and end up looking bad
  const imgSrcs = getImageSources(imageSource, {
    image: {
      height: imageHeightForImageSize[imageSize ?? TileImageSize.Large] * 2,
    },
  });

  return (
    <TileSDS
      title={renderRichTextMarkingsOnly(title)}
      label={renderRichTextMarkingsOnly(label)}
      onClick={onClickWrapped}
      imageSize={imageSize}
      imageFit={imageFit}
      imgSrcs={imgSrcs}
      imgAltText={imageAltText}
      imagePadding={imageSize === TileImageSize.Small && imageFit === TileImageFit.Contain}
      link={url}
      isRTL={isRTL}
    />
  );
};

Tile.displayName = 'Tile';
